import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { Tender } from '../../types/tender';
import { BaseModal } from '../common/UI/BaseModal';

type FeedbackCategory = 'Analysis' | 'Draft' | 'Review';

interface FeedbackOptions {
  Analysis: string[];
  Draft: string[];
  Review: string[];
}

interface FeedbackState {
  Analysis: string[];
  Draft: string[];
  Review: string[];
}

interface FeedbackSubmission extends FeedbackState {
  additionalComments: string;
}

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  tender?: Tender | null;
  onSubmit: (feedback: FeedbackSubmission) => void;
  isLoading: boolean;
}

const feedbackOptions: FeedbackOptions = {
  Analysis: [
    'Bad Strengths/Limitations',
    'Poor Bid Strategy',
    'Missed key requirements',
    'Not detailed enough',
    'Critique is too harsh',
    'Critique is too lenient',
    'Hallucinated facts',
    'Missing documents',
  ],
  Draft: [
    'Wrong tone of voice',
    'Response is too vague',
    'Content is irrelevant',
    "Doesn't address tender",
    'Failed to use company IP',
    'Poorly formatted',
    'Hallucinated facts',
    'Missing major sections',
  ],
  Review: [
    'Suggestions not helpful',
    'Missed critical issues',
    'Critique is too harsh',
    'Critique is too lenient',
    'Failed document comparison',
    'Review is confusing',
    'Suggestions are too minor',
    'Suggestions are unclear',
  ],
};

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  tender,
  onSubmit,
  isLoading,
}) => {
  const [feedback, setFeedback] = useState<FeedbackState>({
    Analysis: ['None'],
    Draft: ['None'],
    Review: ['None'],
  });
  const [additionalComments, setAdditionalComments] = useState('');
  const [charLimit, setCharLimit] = useState(1000);
  const wordLimit = 100;

  useEffect(() => {
    if (open) {
      setFeedback({
        Analysis: ['None'],
        Draft: ['None'],
        Review: ['None'],
      });
      setAdditionalComments('');
    }
  }, [open]);

  useEffect(() => {
    const words = additionalComments.trim().split(/\s+/).filter(Boolean);
    if (words.length <= wordLimit) {
      setCharLimit(100 * 10);
      return;
    }

    let limit = 0;
    let wordsCounted = 0;
    for (let i = 0; i < additionalComments.length; i++) {
      const char = additionalComments[i];
      if (char && /\s/.test(char)) {
        wordsCounted++;
        if (wordsCounted === wordLimit) {
          limit = i;
          break;
        }
      }
    }
    setCharLimit(limit > 0 ? limit : charLimit);
  }, [additionalComments, charLimit]);

  const handleCommentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalComments(event.target.value);
  };

  const handleCheckboxChange = (category: FeedbackCategory, option: string) => {
    setFeedback((prevFeedback) => {
      const currentOptions = [...prevFeedback[category]];
      const isNoneSelected = currentOptions.includes('None');
      const isOtherOption = option !== 'None';

      if (option === 'None') {
        return { ...prevFeedback, [category]: ['None'] };
      }

      let newOptions: string[];
      if (currentOptions.includes(option)) {
        newOptions = currentOptions.filter((item) => item !== option);
      } else {
        newOptions = [...currentOptions, option];
      }

      if (isNoneSelected && isOtherOption) {
        newOptions = newOptions.filter((item) => item !== 'None');
      }

      if (newOptions.length === 0) {
        newOptions = ['None'];
      }

      return { ...prevFeedback, [category]: newOptions };
    });
  };

  const handleSubmit = () => {
    const isAnalysisNone = feedback.Analysis.length === 1 && feedback.Analysis[0] === 'None';
    const isDraftNone = feedback.Draft.length === 1 && feedback.Draft[0] === 'None';
    const isReviewNone = feedback.Review.length === 1 && feedback.Review[0] === 'None';
    const isCommentsEmpty = additionalComments.trim() === '';

    if (isAnalysisNone && isDraftNone && isReviewNone && isCommentsEmpty) {
      onClose();
      return;
    }

    const feedbackToSubmit: FeedbackSubmission = {
      ...feedback,
      additionalComments: additionalComments.trim(),
    };
    onSubmit(feedbackToSubmit);
    onClose();
  };

  const renderCheckboxes = (category: FeedbackCategory) => (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            checked={feedback[category].includes('None')}
            onChange={() => handleCheckboxChange(category, 'None')}
          />
        }
        label="None"
      />
      {feedbackOptions[category].map((option) => (
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              checked={feedback[category].includes(option)}
              onChange={() => handleCheckboxChange(category, option)}
            />
          }
          label={option}
          sx={{
            '& .MuiFormControlLabel-label': {
              whiteSpace: 'normal',
            },
          }}
        />
      ))}
    </FormGroup>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="feedback-dialog"
      BackdropProps={{
        style: {
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}
      title={
        <>
          AI Feedback for:{' '}
          {tender?.title
            ? tender.title.length > 50
              ? `${tender.title.substring(0, 50)}...`
              : tender.title
            : ''}
        </>
      }
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Submit Feedback'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" gutterBottom>
        Your feedback will be used to improve our generative functions and can be updated at any
        time. Select as many options as you'd like.
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box border={1} borderColor="grey.300" borderRadius={1} p={2} height="100%">
            <FormLabel component="legend">Analysis</FormLabel>
            {renderCheckboxes('Analysis')}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box border={1} borderColor="grey.300" borderRadius={1} p={2} height="100%">
            <FormLabel component="legend">Draft</FormLabel>
            {renderCheckboxes('Draft')}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box border={1} borderColor="grey.300" borderRadius={1} p={2} height="100%">
            <FormLabel component="legend">Review</FormLabel>
            {renderCheckboxes('Review')}
          </Box>
        </Grid>
      </Grid>
      <TextField
        label="Additional Comments"
        multiline
        rows={3}
        fullWidth
        value={additionalComments}
        onChange={handleCommentsChange}
        sx={{ mt: 3 }}
        helperText={`${additionalComments.trim().split(/\s+/).filter(Boolean).length}/${wordLimit} words`}
        inputProps={{ maxLength: charLimit }}
      />
    </BaseModal>
  );
};

export default FeedbackDialog;
